import React, { useState } from 'react';
import { AVAILABLE_AVATARS, getAvatarById } from '../../data/avatars';

interface AvatarSelectorProps {
    currentAvatarId?: string;
    onAvatarSelect: (avatarId: string) => void;
    onClose: () => void;
    isVisible: boolean;
}

/**
 * Componente selector de avatares para niños
 */
const AvatarSelector: React.FC<AvatarSelectorProps> = ({
    currentAvatarId,
    onAvatarSelect,
    onClose,
    isVisible
}) => {
    const [selectedAvatarId, setSelectedAvatarId] = useState<string>(currentAvatarId || '');

    if (!isVisible) return null;

    const handleAvatarSelect = (avatarId: string) => {
        setSelectedAvatarId(avatarId);
    };

    const handleConfirmSelection = () => {
        if (selectedAvatarId) {
            onAvatarSelect(selectedAvatarId);
            onClose();
        }
    };

    const handleClose = () => {
        setSelectedAvatarId(currentAvatarId || '');
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-center text-white">
                    <h2 className="text-3xl font-bold mb-2">
                        🎭 Selecciona tu Avatar
                    </h2>
                    <p className="text-lg opacity-90">
                        Elige el personaje que más te represente
                    </p>
                </div>

                {/* Contenido */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {/* Avatar seleccionado actualmente */}
                    {currentAvatarId && (
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6 border border-blue-200">
                            <h4 className="font-medium text-blue-700 mb-3 text-center text-lg">
                                🎯 Tu Avatar Actual
                            </h4>
                            <div className="flex items-center justify-center space-x-4">
                                <div className="w-20 h-20 rounded-full overflow-hidden bg-white border-4 border-blue-300 shadow-lg">
                                    <img
                                        src={getAvatarById(currentAvatarId)?.path || ''}
                                        alt="Avatar actual"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="text-center">
                                    <p className="font-bold text-blue-800 text-lg">
                                        {getAvatarById(currentAvatarId)?.name}
                                    </p>
                                    <p className="text-sm text-blue-600">
                                        Avatar seleccionado actualmente
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Grid de avatares */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        {AVAILABLE_AVATARS.map((avatar) => (
                            <div
                                key={avatar.id}
                                onClick={() => handleAvatarSelect(avatar.id)}
                                className={`relative cursor-pointer rounded-2xl p-4 border-4 transition-all duration-200 transform hover:scale-105 ${
                                    selectedAvatarId === avatar.id
                                        ? 'border-blue-500 bg-blue-50 shadow-lg ring-4 ring-blue-200'
                                        : avatar.id === currentAvatarId
                                        ? 'border-green-400 bg-green-50 shadow-md'
                                        : 'border-gray-200 hover:border-blue-300 bg-white hover:shadow-md'
                                }`}
                            >
                                {/* Avatar seleccionado */}
                                {selectedAvatarId === avatar.id && (
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg">
                                        ✓
                                    </div>
                                )}

                                {/* Indicador de avatar actual */}
                                {avatar.id === currentAvatarId && selectedAvatarId !== avatar.id && (
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg">
                                        👤
                                    </div>
                                )}

                                {/* Imagen del avatar */}
                                <div className="w-full aspect-square mb-3 rounded-xl overflow-hidden bg-gray-100">
                                    <img
                                        src={avatar.path}
                                        alt={avatar.name}
                                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-200"
                                    />
                                </div>

                                {/* Nombre del avatar */}
                                <h3 className="font-semibold text-gray-800 text-center text-sm mb-1">
                                    {avatar.name}
                                </h3>

                                {/* Descripción */}
                                <p className="text-xs text-gray-600 text-center leading-tight">
                                    {avatar.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer con botones */}
                <div className="bg-gray-50 p-6 flex flex-col sm:flex-row gap-3 justify-end">
                    <button
                        onClick={handleClose}
                        className="px-6 py-3 bg-gray-500 text-white rounded-xl font-medium hover:bg-gray-600 transition-colors duration-200"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleConfirmSelection}
                        disabled={!selectedAvatarId}
                        className={`px-8 py-3 rounded-xl font-medium transition-all duration-200 ${
                            selectedAvatarId
                                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 shadow-lg'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {currentAvatarId && selectedAvatarId === currentAvatarId
                            ? 'Mantener Actual'
                            : currentAvatarId
                            ? 'Cambiar Avatar'
                            : 'Seleccionar Avatar'
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AvatarSelector;
